import { assertNever } from '../lib/assert-never.js';
import type { ExperienceBlockDraft, ExperiencePageDraft } from '../schemas/experience.js';
import type {
  ExitAction,
  ExitCondition,
  ExitTrigger,
  ProtocolObjectiveDraft,
  ProtocolStepDraft,
  StepFallback,
} from '../schemas/protocol.js';
import type { ExperienceTokenRemap, IdRemap } from './remap.js';
import {
  buildIdRemap,
  remapDisplayCondition,
  remapExperienceTokens,
  remapId,
  remapProtocolTokens,
  remapRichTextTokens,
} from './remap.js';

/**
 * The CLONES of §4.2 (grill of 2026-09-21; issue #115): one typed function per
 * item a screen offers to duplicate, each obeying the single rule stated at the
 * top of `remap.ts` - mint inside, readdress inside, never touch outside.
 *
 * ## Why these five, and not seven
 *
 * The grill lists seven duplicable items. Five carry local ids and are here: a
 * protocol step, an objective, an exit condition, an experience page, a block.
 * The two that are missing are missing on purpose:
 *
 * - a criterion EXAMPLE is a bare `string` (`criterionExampleSchema`), so its
 *   clone would be the identity function;
 * - a GLOBAL RULE is written through `globalRuleBlockRowSchema`, whose `id` is
 *   ABSENT to mean « create this one » (§11.3): the store mints, and a clone
 *   here would mint an id the wire then throws away.
 *
 * Both would be an abstraction over a one-line operation, which this repo does
 * not add. The screens duplicate them by copying the value and letting the
 * existing save do the rest.
 *
 * ## What a clone does NOT do: name it
 *
 * A copy's NAME is the other pure function of this lot (`copy-name.ts`), and it
 * stays out of here because it needs something a subtree does not hold: the
 * list of names already taken - the non-retired entities for an entity, the
 * whole timeline for a variable name (§8.6). The caller composes the two, which
 * also lets it rename several blocks in one pass while accumulating what it has
 * just taken.
 *
 * ## Drafts in, drafts out
 *
 * Every signature takes the DRAFT type (`ProtocolStepDraft`,
 * `ExperiencePageDraft`, ...), because duplication is an editor gesture and an
 * editor holds a draft (§4). The version types are structurally identical - the
 * draft only relaxes bounds Zod checks, never the TypeScript shape - so a
 * caller holding a version's step may pass it unchanged.
 *
 * Each clone writes every field of its node explicitly rather than spreading
 * the source. It is more lines, and it buys the one thing a spread cannot: a
 * field added to a schema tomorrow fails to compile HERE, where somebody has to
 * decide whether it carries a reference, instead of being copied as-is with its
 * stale ids inside.
 *
 * ## The one exception, named rather than hidden
 *
 * Two fields ARE spread: `visual` on a page (`experienceThemeSchema`, §9.4) and
 * `visual` on a block (`blockVisualSchema`, §9.2). Both are flat records of
 * presentation scalars - fonts, colours, alignments, speeds - listing which
 * would put twenty-five optional fields in the middle of a rule about
 * references, and each one of them would still be copied verbatim.
 *
 * The theme does carry ONE reference, `backgroundImageAssetId`, and copying it
 * as it stands is the right answer rather than an oversight: a media file
 * belongs to the library and not to the page (§9.1), so it is OUTSIDE the
 * subtree by nature and two pages citing one file is the normal state of an
 * experience. What the spread costs is the compile error: a reference-bearing
 * field added to either shape LATER would ride along unnoticed. That is the
 * trade, and it is written here so the next person to widen those two shapes
 * reads it.
 */

/** What a clone answers: the copy, and the tables that made it. */
export type Cloned<Node, Remap> = {
  readonly clone: Node;
  readonly remap: Remap;
};

/* ---------------------------------------------------------------- protocol */

/**
 * The three namespaces a step's subtree mints (§7.2, §8.4).
 *
 * `conditions` is minted and never read back: no reference in a content names a
 * condition (`protocolJumps` carries a condition id in its computed result, not
 * in the stored content), and a step copy would not even collide - `refuseDuplicateIds`
 * scopes condition ids to their STEP, where step ids and objective ids are
 * checked protocol-wide. It is minted anyway, and the reason is the rule itself:
 * « un identifiant neuf est frappé pour chaque nœud du sous-arbre copié »
 * (§4.2). A copy sharing its conditions' ids with its source would be two nodes
 * at one address the day anything starts naming them - a per-condition note, a
 * deep link, a step whose conditions are moved rather than copied.
 */
export type ProtocolStepRemap = {
  /** The step itself: what a `go-to-step` or a fallback aimed AT IT follows. */
  readonly steps: IdRemap;
  /** Its objectives: what `{{objectif:...}}`, a status trigger and an invalidation follow. */
  readonly objectives: IdRemap;
  /** Its exit conditions. */
  readonly conditions: IdRemap;
};

/**
 * A step and everything under it, ready to be inserted beside the original.
 *
 * The four cases that make this function worth its tests, all of them from
 * §7.2, §8.4 and H20:
 *
 * - the step's direction quotes one of ITS OWN objectives - inside, readdressed,
 *   or the copy would feed the interviewer the original's matter;
 * - a trigger names an objective of an EARLIER step, which H20 allows - outside,
 *   left alone, and readdressing it would silently rewire the protocol;
 * - an action « aller à une étape » aims at ANOTHER step - outside, left alone;
 * - the step aims at ITSELF, by an action or by its fallback - inside, so it
 *   follows the copy. A replayed copy that sent the session back to the original
 *   would be a loop nobody drew.
 */
export function cloneProtocolStep(
  step: ProtocolStepDraft,
  mintId: () => string,
): Cloned<ProtocolStepDraft, ProtocolStepRemap> {
  const remap: ProtocolStepRemap = {
    steps: buildIdRemap([step.id], mintId),
    objectives: buildIdRemap(
      step.objectives.map((objective) => objective.id),
      mintId,
    ),
    conditions: buildIdRemap(
      step.conditions.map((condition) => condition.id),
      mintId,
    ),
  };

  const clone: ProtocolStepDraft = {
    id: remapId(remap.steps, step.id),
    name: step.name,
    // The copy of a farewell step is a farewell step (§4.2: a duplicate is the
    // whole step, its nature included).
    farewell: step.farewell,
    // The reread on entry is the step's too (issue #165, H93).
    rereadOnEntry: step.rereadOnEntry,
    // So is hiding the objectives from the interviewer (issue #191, H18).
    hideObjectives: step.hideObjectives,
    direction: remapProtocolTokens(step.direction, remap.objectives),
    objectives: step.objectives.map((objective) => remapObjective(objective, remap.objectives)),
    rules: {
      maxTurns: step.rules.maxTurns,
      emptyAnswersAllowed: step.rules.emptyAnswersAllowed,
      attempts: step.rules.attempts,
      fallback: remapFallback(step.rules.fallback, remap.steps),
    },
    conditions: step.conditions.map((condition) => remapCondition(condition, remap)),
  };

  return { clone, remap };
}

/**
 * One objective, duplicated inside its step.
 *
 * The same `remapObjective` the step clone runs, with a table holding one
 * entry - which is the whole point of writing the rule as a table rather than
 * as a branch. The consequence is worth stating: an objective whose direction
 * quotes ITSELF sees that quote follow the copy, while a quote of a SIBLING
 * objective - not copied, therefore outside - is left alone. The criteria it
 * requires are `{ id, pin }` references to criterion ENTITIES (§4): outside by
 * nature, and never minted.
 */
export function cloneProtocolObjective(
  objective: ProtocolObjectiveDraft,
  mintId: () => string,
): Cloned<ProtocolObjectiveDraft, IdRemap> {
  const objectives = buildIdRemap([objective.id], mintId);
  return { clone: remapObjective(objective, objectives), remap: objectives };
}

/**
 * One exit condition, duplicated inside its step.
 *
 * Its triggers and its actions name objectives and steps that all stay where
 * they are - the subtree is the condition alone - so nothing but its own id
 * changes. It is still the shared `remapCondition`, run with two empty tables,
 * rather than a second spelling of the same walk.
 */
export function cloneExitCondition(
  condition: ExitCondition,
  mintId: () => string,
): Cloned<ExitCondition, IdRemap> {
  const conditions = buildIdRemap([condition.id], mintId);
  const remap: ProtocolStepRemap = {
    steps: new Map<string, string>(),
    objectives: new Map<string, string>(),
    conditions,
  };
  return { clone: remapCondition(condition, remap), remap: conditions };
}

function remapObjective(
  objective: ProtocolObjectiveDraft,
  objectives: IdRemap,
): ProtocolObjectiveDraft {
  return {
    id: remapId(objectives, objective.id),
    name: objective.name,
    mandatory: objective.mandatory,
    direction: remapProtocolTokens(objective.direction, objectives),
    criteria: objective.criteria.map((requirement) => ({
      ref: { id: requirement.ref.id, pin: requirement.ref.pin },
      threshold: requirement.threshold,
      // The « l'un de » label is a datum of the citation (issue #181): dropped
      // here, a copied objective would silently turn its groups into plain
      // criteria. Spread only when set, for `exactOptionalPropertyTypes`.
      ...(requirement.orGroup === undefined ? {} : { orGroup: requirement.orGroup }),
    })),
    quantity: {
      minSentences: objective.quantity.minSentences,
      minChars: objective.quantity.minChars,
    },
    // The level the analyst reads the objective at is the objective's own datum
    // (analyste.md §3.1, issue #196): a copied step keeps the levels of its
    // objectives. The two readings of the section are the protocol's and cite
    // objectives by id: what points outside the copy does not move.
    analystLevel: objective.analystLevel,
  };
}

function remapCondition(condition: ExitCondition, remap: ProtocolStepRemap): ExitCondition {
  return {
    id: remapId(remap.conditions, condition.id),
    groups: condition.groups.map((group) =>
      group.map((trigger) => remapTrigger(trigger, remap.objectives)),
    ),
    actions: condition.actions.map((action) => remapAction(action, remap)),
  };
}

/**
 * The closed list of triggers (§8.4), one branch each.
 *
 * Written out rather than spread so that an eighth trigger - one naming a step,
 * say - cannot reach production without somebody deciding here whether it
 * carries a reference. `assertNever` is what turns that into a compile error.
 */
function remapTrigger(trigger: ExitTrigger, objectives: IdRemap): ExitTrigger {
  switch (trigger.kind) {
    case 'objective-status':
      return {
        kind: 'objective-status',
        objectiveId: remapId(objectives, trigger.objectiveId),
        to: trigger.to,
      };
    case 'objective-class':
      return { kind: 'objective-class', which: trigger.which };
    case 'turn-cap-reached':
      return { kind: 'turn-cap-reached' };
    case 'attempts-exhausted':
      return { kind: 'attempts-exhausted' };
    case 'empty-answers-exceeded':
      return { kind: 'empty-answers-exceeded' };
    default:
      return assertNever(trigger, 'exit trigger');
  }
}

/** The closed list of actions (§8.4). Three of the seven carry a reference. */
function remapAction(action: ExitAction, remap: ProtocolStepRemap): ExitAction {
  switch (action.kind) {
    case 'direction':
      return { kind: 'direction', text: remapProtocolTokens(action.text, remap.objectives) };
    case 'invalidate-objective':
      return {
        kind: 'invalidate-objective',
        objectiveId: remapId(remap.objectives, action.objectiveId),
      };
    case 'go-to-step':
      return { kind: 'go-to-step', stepId: remapId(remap.steps, action.stepId) };
    case 'close-step':
      return { kind: 'close-step' };
    case 'replay-step':
      return { kind: 'replay-step' };
    case 'close-protocol':
      return { kind: 'close-protocol' };
    case 'close-experience':
      return { kind: 'close-experience', text: action.text };
    default:
      return assertNever(action, 'exit action');
  }
}

function remapFallback(fallback: StepFallback, steps: IdRemap): StepFallback {
  switch (fallback.kind) {
    case 'close-experience':
      return { kind: 'close-experience', text: fallback.text };
    case 'go-to-step':
      return { kind: 'go-to-step', stepId: remapId(steps, fallback.stepId) };
    default:
      return assertNever(fallback, 'step fallback');
  }
}

/* -------------------------------------------------------------- experience */

/** The three namespaces a page's subtree mints (§9.2, §9.3). */
export type ExperiencePageRemap = {
  /** The page itself: what `{{choix:<pageId>}}` would follow. */
  readonly pages: IdRemap;
  /** Its temps. Nothing in a content references a temps. */
  readonly temps: IdRemap;
  /** Its blocks: what `{{champ:<blockId>}}` would follow. */
  readonly blocks: IdRemap;
};

/**
 * A page with its temps and its blocks.
 *
 * The property worth knowing, and the reason the tests PROVE it rather than
 * assume it: the rewriting this clone performs on the page's texts is always
 * EMPTY. Not because a page has no variables, but because of H60 (§8.6, grill
 * Variables D3) - a value exists only from the page AFTER the one that produces
 * it, so a page can never cite its own field or its own choice, and
 * `variableScopeBreach` refuses such a content at « Enregistrer une nouvelle
 * version ». Every `{{champ:...}}` and `{{choix:...}}` a legal page carries
 * therefore names a block or a page that sits EARLIER in the timeline, outside
 * the subtree, and is left alone.
 *
 * The tables are still threaded through the rewriting rather than skipped. The
 * cost is one `Map` lookup per token; what it buys is that the rule holds
 * without an exception nobody would maintain - the day §8.6 lets a page read
 * something of its own, this function is already right.
 *
 * The PROTOCOL page is not duplicable - §4.2 puts it first under « ce qui ne se
 * duplique pas », because a second one « est un état que §9.5 ne sait pas lire »
 * - and this function does not pretend otherwise: it copies the `kind` it is
 * given. Coercing it to `content` would be worse than useless, since it would
 * answer a gesture nobody should have offered with a page whose nature changed
 * in silence. The screen is what does not offer the gesture (the icon is absent,
 * as it is in read-only), and `refuseBrokenStructure` is the net under it.
 *
 * The FEEDBACK page (issue #176, retour-experience.md §2) is in the same case:
 * it does not duplicate, a second one being the refusal `feedback-repeated`. This
 * function copies its `kind` and its `thanks` as it copies the protocol page's
 * `kind`, and `pageOffersDuplicate` - `kind === 'content'` - is what keeps the
 * gesture off it. What ADDS one is `withFeedbackPage` (`experience/template.ts`).
 */
export function cloneExperiencePage(
  page: ExperiencePageDraft,
  mintId: () => string,
): Cloned<ExperiencePageDraft, ExperiencePageRemap> {
  const remap: ExperiencePageRemap = {
    pages: buildIdRemap([page.id], mintId),
    temps: buildIdRemap(
      page.temps.map((temps) => temps.id),
      mintId,
    ),
    blocks: buildIdRemap(
      page.temps.flatMap((temps) => temps.blocks.map((block) => block.id)),
      mintId,
    ),
  };
  const tokens: ExperienceTokenRemap = { fields: remap.blocks, choices: remap.pages };

  const clone: ExperiencePageDraft = {
    id: remapId(remap.pages, page.id),
    name: page.name,
    kind: page.kind,
    autoDurationSeconds: page.autoDurationSeconds,
    transition: { animation: page.transition.animation, speed: page.transition.speed },
    visual: { ...page.visual },
    temps: page.temps.map((temps) => ({
      id: remapId(remap.temps, temps.id),
      speed: temps.speed,
      start:
        temps.start.kind === 'delay'
          ? { kind: 'delay', seconds: temps.start.seconds }
          : { kind: 'click' },
      blocks: temps.blocks.map((block) =>
        remapBlock(block, remapId(remap.blocks, block.id), tokens),
      ),
    })),
    protocol: page.protocol === null ? null : { id: page.protocol.id, pin: page.protocol.pin },
    thanks: page.thanks,
    conversation: page.conversation === null ? null : { context: page.conversation.context },
  };

  return { clone, remap };
}

/**
 * One block, duplicated inside its temps.
 *
 * Its own id is the only thing that changes. A block can no more cite itself
 * than a page can (H60 again, and more plainly: a block's value does not exist
 * while the block is on screen), so the table is threaded through for the same
 * reason as above and rewrites nothing in practice.
 *
 * The variable NAME is copied as it stands, duplicate and all. Suffixing it is
 * `nextFreeVariableName`'s job and the caller's call: the timeline is what says
 * which names are taken (§8.6), and a block does not hold the timeline.
 *
 * And the caller suffixes an INPUT or a CHECKBOX, never a BUTTON. §4.2 names
 * the Input, and §8.6 says why the button is different: a page remembers ONE
 * choice, « une seule par page », and every remembering button of that page
 * carries the SAME name - they are the issues of one choice, not two variables.
 * `pageChoiceName` reads the first named one, so renaming a duplicated button
 * would not create a variable, it would create a dead label: a name no
 * `{{choix:...}}` can reach, that `namedVariablesOf` never lists, and that the
 * inspector shows beside the original's on what is one variable.
 *
 * That last sentence is about THIS gesture and not about the button, and §4.2
 * cuts the rule by gesture rather than by type: duplicating a whole PAGE
 * re-suffixes its choice ONCE, on every remembering button of the copy at
 * once - the copy is a second page, so it remembers a second choice - and
 * duplicating the ONLY button of a page makes a choice exist where there was
 * none, so that one is suffixed if it collides. What stays true here is the
 * narrow case this function serves: one more button on a page that already
 * writes its choice renames nothing.
 */
export function cloneExperienceBlock(
  block: ExperienceBlockDraft,
  mintId: () => string,
): Cloned<ExperienceBlockDraft, IdRemap> {
  const blocks = buildIdRemap([block.id], mintId);
  const tokens: ExperienceTokenRemap = { fields: blocks, choices: new Map<string, string>() };
  return { clone: remapBlock(block, remapId(blocks, block.id), tokens), remap: blocks };
}

/**
 * The eight types of §9.2, one branch each, for the reason `remapTrigger` gives.
 *
 * The two of issue #137 - a Lien, un Texte copiable - both carry text that
 * ACCEPTS variables, so both go through `remapExperienceTokens` like a
 * Checkbox's label: a page duplicated with them has to cite the copy's blocks
 * and not the original's. A Lien's `url` is copied verbatim and never remapped:
 * no token may live there in the first place (`linkUrlRefusal`), so a remap
 * would be a pass over a string that can only be literal. Neither carries a
 * `variableName`, so neither is ever suffixed by the caller. A Bouton's waiting
 * label (#174) is copied verbatim like its label: neither accepts a variable.
 *
 * The display conditions of §9.2 are copied with every block, readdressed by the
 * same rule as a token (`remapDisplayCondition`). Two fields of issue #147 are
 * NOT copied as they stand, and on purpose: an Input's « Nom de la personne dans
 * Sessions » comes back FALSE - there is at most one per experience (H75), so a
 * copy that kept it would be refused by the next save, on a gesture nobody made
 * - and a Noyau block keeps its mode and its gap threshold (#169), which the
 * save then judges like any other (a second first choice is refused by name,
 * `noyau-choice-repeated`).
 */
function remapBlock(
  block: ExperienceBlockDraft,
  id: string,
  tokens: ExperienceTokenRemap,
): ExperienceBlockDraft {
  const base = {
    id,
    animation: block.animation,
    visual: { ...block.visual },
    conditions: block.conditions.map((condition) => remapDisplayCondition(condition, tokens)),
  };
  switch (block.type) {
    case 'text':
      return { ...base, type: 'text', text: remapRichTextTokens(block.text, tokens) };
    case 'image':
      return { ...base, type: 'image', assetId: block.assetId, caption: block.caption };
    case 'sound':
      return {
        ...base,
        type: 'sound',
        assetId: block.assetId,
        loop: block.loop,
        volume: block.volume,
      };
    case 'button':
      return {
        ...base,
        type: 'button',
        label: block.label,
        action: block.action,
        waitingLabel: block.waitingLabel,
        rememberChoice: block.rememberChoice,
        variableName: block.variableName,
      };
    case 'checkbox':
      return {
        ...base,
        type: 'checkbox',
        label: remapExperienceTokens(block.label, tokens),
        required: block.required,
        variableName: block.variableName,
      };
    case 'input':
      return {
        ...base,
        type: 'input',
        label: block.label,
        lines: block.lines,
        required: block.required,
        format: block.format,
        variableName: block.variableName,
        personName: false,
      };
    case 'link':
      return {
        ...base,
        type: 'link',
        label: remapExperienceTokens(block.label, tokens),
        url: block.url,
      };
    case 'copyable':
      return { ...base, type: 'copyable', text: remapExperienceTokens(block.text, tokens) };
    case 'noyau':
      return { ...base, type: 'noyau', mode: block.mode, maxGap: block.maxGap };
    // The question of a Sélection and of a Note accepts variables, so it is
    // readdressed like a Checkbox's label; the options are plain text, copied
    // as they stand. The caller suffixes the variable name, as for an Input.
    case 'selection':
      return {
        ...base,
        type: 'selection',
        question: remapExperienceTokens(block.question, tokens),
        options: [...block.options],
        required: block.required,
        variableName: block.variableName,
      };
    case 'note':
      return {
        ...base,
        type: 'note',
        question: remapExperienceTokens(block.question, tokens),
        required: block.required,
        variableName: block.variableName,
      };
    default:
      return assertNever(block, 'experience block');
  }
}
